import { clsx } from "clsx";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
    SelectedState,
    getConsentLevel,
} from "web/react/components/cookie-settings/cookie-settings";
import SvgIcon from "web/react/components/svg-icon/svg-icon";
import Switch from "web/react/components/switch/switch";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import withReduxProvider from "web/react/redux-provider";
import { setCookieConsent } from "web/redux/ducks/cookie-consent";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import * as styles from "./ccpa-option.css";

function mapStateToProps(state): Record<string, any> {
    const { acceptsCustomization, acceptsAds, acceptsAnalytics } = state.cookieConsentReducer;
    return {
        acceptsCustomization,
        acceptsAds,
        acceptsAnalytics,
    };
}

enum OptInType {
    sell = "sell",
    share = "share",
}

interface CcpaOptionProps {
    acceptsCustomization: boolean;
    acceptsAds: boolean;
    acceptsAnalytics: boolean;
}

function getCurrentSelectedState(): SelectedState {
    const sellControllerID = "cookie-controller-sell";
    const shareControllerID = "cookie-controller-share";

    const sellControllerElement = document.getElementById(sellControllerID);
    const shareControllerElement = document.getElementById(shareControllerID);
    return {
        customizationCookie: sellControllerElement?.dataset.checked === "true" || false,
        adCookie: shareControllerElement?.dataset.checked === "true" || false,
        analyticalCookie: sellControllerElement?.dataset.checked === "true" || false,
    };
}

function CcpaOption({
    acceptsCustomization,
    acceptsAds,
    acceptsAnalytics,
}: CcpaOptionProps): JSX.Element {
    const dispatch = useDispatch();

    const [optOutSell, setOptOutSell] = useState(false);
    const [updatedSell, setUpdatedSell] = useState(false);

    const [optOutShare, setOptOutShare] = useState(false);
    const [updatedShare, setUpdatedShare] = useState(false);

    useEffect(() => {
        setOptOutSell(!acceptsCustomization || !acceptsAnalytics);
        setOptOutShare(!acceptsAds);
    }, [acceptsCustomization, acceptsAnalytics, acceptsAds]);

    function toggleSellOptOption(optOut): void {
        /*
         * When the user toggles the "sell" option they are opting in/out of customizationCookie and analyticsCookie
         * Args:
         *   optOut (boolean): the new state of the toggle as it's clicked by the user
         */
        setUpdatedSell(!updatedSell);
        const currentSelectedState = getCurrentSelectedState();
        if (optOut) {
            analytics.event("ccpa_toggle_sell", "clicked", "rejected_cookies");
            const cookieConsent = {
                customizationCookie: getConsentLevel(currentSelectedState.customizationCookie),
                adCookie: getConsentLevel(!currentSelectedState.adCookie),
                personalizedAdsCookie: getConsentLevel(!currentSelectedState.adCookie),
                analyticsCookie: getConsentLevel(currentSelectedState.analyticalCookie),
            };
            dispatch(setCookieConsent(cookieConsent));
        } else {
            analytics.event("ccpa_toggle_sell", "clicked", "accepts_cookies");
            const cookieConsent = {
                customizationCookie: getConsentLevel(currentSelectedState.customizationCookie),
                adCookie: getConsentLevel(!currentSelectedState.adCookie),
                personalizedAdsCookie: getConsentLevel(!currentSelectedState.adCookie),
                analyticsCookie: getConsentLevel(currentSelectedState.analyticalCookie),
            };

            dispatch(setCookieConsent(cookieConsent));
        }
    }

    function toggleShareOptOption(optOut): void {
        /*
         * When the user toggles the "share" option they are opting in/out of the adCookie
         * Args:
         *   optOut (boolean): the new state of the toggle as it's clicked by the user
         */
        setUpdatedShare(!updatedShare);
        const currentSelectedState = getCurrentSelectedState();

        if (optOut) {
            analytics.event("ccpa_toggle_share", "clicked", "rejected_cookies");
            const cookieConsent = {
                customizationCookie: getConsentLevel(!currentSelectedState.customizationCookie),
                adCookie: getConsentLevel(currentSelectedState.adCookie),
                personalizedAdsCookie: getConsentLevel(currentSelectedState.adCookie),
                analyticsCookie: getConsentLevel(!currentSelectedState.analyticalCookie),
            };
            dispatch(setCookieConsent(cookieConsent));
        } else {
            analytics.event("ccpa_toggle_share", "clicked", "accepts_cookies");
            const cookieConsent = {
                customizationCookie: getConsentLevel(!currentSelectedState.customizationCookie),
                adCookie: getConsentLevel(currentSelectedState.adCookie),
                personalizedAdsCookie: getConsentLevel(currentSelectedState.adCookie),
                analyticsCookie: getConsentLevel(!currentSelectedState.analyticalCookie),
            };

            dispatch(setCookieConsent(cookieConsent));
        }
    }

    function getOptInOptionIcon(optionType: OptInType): JSX.Element {
        return optionType === OptInType.sell ? (
            <SvgIcon name={updatedSell ? "tick" : "info-icon"} />
        ) : (
            <SvgIcon name={updatedShare ? "tick" : "info-icon"} />
        );
    }

    function getOptInOptionText(optionType: OptInType): string {
        if (optionType === OptInType.sell) {
            return optOutSell
                ? // "You have opted-out of selling your data"
                  gettext("privacy_policy.california_privacy_rights.opted_out.label")
                : //   "You are opted-in on selling your data"
                  gettext("privacy_policy.california_privacy_rights.opted_in.label");
        }

        return optOutShare
            ? // "You have opted-out of sharing your personal data"
              gettext("privacy_policy.california_privacy_rights.opted_out_sharing.label")
            : // "You are opted-in on sharing your personal data"
              gettext("privacy_policy.california_privacy_rights.opted_in_sharing.label");
    }

    const oneTrustEnabled = environment.getFeature("web_onetrust_enabled") as boolean;
    const { isDomLoaded } = useDomViewport();
    if (isDomLoaded && oneTrustEnabled) {
        return (
            <div className={styles.optOutWidgetContainer}>
                <p>{gettext("privacy_policy.california_privacy_rights.cookies_link")}</p>
                <a
                    id="ot-sdk-btn"
                    // styling applied directly in cookie management platform
                    className={clsx(styles.link, "ot-sdk-show-settings")}
                >
                    {gettext("privacy_policy.california_privacy_rights.do_not_sell_or_share.label")}
                </a>
            </div>
        );
    } else {
        return (
            <div className={styles.optOutWidgetContainer}>
                <div className={styles.optOutWidget}>
                    {/* You can opt-out or opt-in from the selling of your personal information according to the terms of CCPA legislation using the checkbox below. */}
                    <p>{gettext("privacy_policy.california_privacy_rights.opt_out.title")}</p>

                    <div className={styles.optOutSelector}>
                        {/*  Do not sell my personal information  */}
                        <p>
                            {gettext("privacy_policy.california_privacy_rights.do_not_sell.label")}
                        </p>

                        <Switch
                            elementId="cookie-controller-sell"
                            selected={optOutSell}
                            disabled={false}
                            onChange={toggleSellOptOption}
                        />
                    </div>

                    <div className={styles.optOutSelector}>
                        {/*  Do not share my personal information  */}
                        <p>
                            {gettext("privacy_policy.california_privacy_rights.do_not_share.label")}
                        </p>

                        <Switch
                            elementId="cookie-controller-share"
                            selected={optOutShare}
                            disabled={false}
                            onChange={toggleShareOptOption}
                        />
                    </div>
                </div>

                <div className={styles.ccpaOptSelectedOption}>
                    <span>
                        {getOptInOptionIcon(OptInType.sell)}
                        {gettext(getOptInOptionText(OptInType.sell))}
                    </span>
                </div>

                <div className={styles.ccpaOptSelectedOption}>
                    <span>
                        {getOptInOptionIcon(OptInType.share)}
                        {gettext(getOptInOptionText(OptInType.share))}
                    </span>
                </div>

                <div className={styles.ccpaAdditionalInfo}>
                    {/* You can change your preferences at any time by visiting “Do not sell or share my personal information” in the bottom of the page. */}
                    {gettext(
                        "privacy_policy.california_privacy_rights.change_preferences_2023.label"
                    )}
                </div>
            </div>
        );
    }
}

export default withReduxProvider(connect(mapStateToProps)(CcpaOption));
